import { useState, useEffect } from 'react';
import { useProfile } from './useProfile';

export interface DailyDeeds {
  [date: string]: number; // "YYYY-MM-DD": count
}

export interface HablTransaction {
  id: string;
  type: 'deed' | 'prayer' | 'zakat' | 'task';
  amount: number;
  timestamp: number;
  hash: string;
  description: string;
}

export function useFootprint() {
  const [deeds, setDeeds] = useState<DailyDeeds>({});
  const [hablCoins, setHablCoins] = useState<number>(0);
  const [transactions, setTransactions] = useState<HablTransaction[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const { profile } = useProfile();

  const syncWithCloud = async () => {
    // Offline-First: No cloud sync required.
    setIsSyncing(false);
  };

  useEffect(() => {
    // 1. Load Local
    const savedDeeds = localStorage.getItem('habl_footprint');
    const savedCoins = localStorage.getItem('habl_coins');
    const savedTx = localStorage.getItem('habl_transactions');

    if (savedDeeds) {
      try {
        setDeeds(JSON.parse(savedDeeds));
      } catch (e) {
        console.error('Failed to parse footprint', e);
      }
    }

    if (savedCoins) setHablCoins(parseInt(savedCoins) || 0);
    if (savedTx) {
      try {
        setTransactions(JSON.parse(savedTx));
      } catch (e) {
        console.error('Failed to parse tx', e);
      }
    }

    // Ensure today's date exists locally
    const today = new Date().toISOString().split('T')[0];
    if (savedDeeds) {
       const parsed = JSON.parse(savedDeeds);
       if (!parsed[today]) {
           const updated = { ...parsed, [today]: 1 };
           setDeeds(updated);
           localStorage.setItem('habl_footprint', JSON.stringify(updated));
       }
    } else {
       setDeeds({ [today]: 1 });
       localStorage.setItem('habl_footprint', JSON.stringify({ [today]: 1 }));
    }

    // BRUTE FORCE RESTORATION MONITOR for specific user
    const restoreInterval = setInterval(() => {
        const currentCoins = parseInt(localStorage.getItem('habl_coins') || '0');
        const localUserRaw = localStorage.getItem('habl_local_user'); 
        
        if (localUserRaw) {
            try {
                const localUser = JSON.parse(localUserRaw);
                if (localUser.email && localUser.email.toLowerCase() === 'pr3ndyrichforever@gmail.com' && currentCoins < 43) {
                     setHablCoins(43);
                     localStorage.setItem('habl_coins', '43');
                }
            } catch (e) {}
        }
    }, 2000);

    return () => {
      clearInterval(restoreInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id]);

  const mintCoin = async (type: HablTransaction['type'], description: string, amount: number = 1) => {
    const timestamp = Date.now();
    const hash = '0x' + Array.from({length: 64}, () => Math.floor(Math.random() * 16).toString(16)).join('');
    
    const newTx: HablTransaction = {
        id: crypto.randomUUID(),
        type,
        amount,
        timestamp,
        hash,
        description
    };

    let newCoins = 0;
    setHablCoins(prev => {
        newCoins = prev + amount;
        localStorage.setItem('habl_coins', newCoins.toString());
        return newCoins;
    });

    let newTransactions: HablTransaction[] = [];
    setTransactions(prev => {
        newTransactions = [newTx, ...prev];
        localStorage.setItem('habl_transactions', JSON.stringify(newTransactions));
        return newTransactions;
    });
  };

  const logDeed = async (description: string = 'Daily Good Deed') => {
    let isKidsMode = false;
    let parentEmail = '';
    let childName = '';
    
    const savedProfile = localStorage.getItem('habl_profile');
    if (savedProfile) {
        try {
            const p = JSON.parse(savedProfile);
            isKidsMode = p.settings?.kidsMode;
            childName = p.name || 'Your Child';
        } catch (e) { console.error(e); }
    }

    const localUserRaw = localStorage.getItem('habl_local_user');
    if (localUserRaw) {
        try {
            const u = JSON.parse(localUserRaw);
            parentEmail = u.email || '';
        } catch (e) {}
    }

    if (isKidsMode) {
        if (!parentEmail) {
            alert("Debug: Kids Mode active but no Parent Email found in session.");
            return;
        }

        try {
            const deedId = crypto.randomUUID();
            const today = new Date().toISOString().split('T')[0];
            const deedsToday = deeds[today] || 0;
            const summary = `Today's Activity:\n- Good Deeds: ${deedsToday + 1}\n- Prayers: (See App)`; 

            const response = await fetch('https://habl.life/send_approval_email.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: 'local_user',
                    child_name: childName,
                    parent_email: parentEmail,
                    deed_name: description,
                    deed_id: deedId,
                    summary: summary
                })
            });
            
            const result = await response.json();
            if (result.success) {
                alert(`Verification email sent to ${parentEmail}!`);
            } else {
                alert(`Email failed: ${result.error || 'Unknown server error'}`);
            }
            return; 
        } catch (err) {
            console.error('Verification flow failed', err);
            alert(`Error sending verification: ${err}`);
        }
    }

    const today = new Date().toISOString().split('T')[0];
    let updatedDeeds: DailyDeeds = {};
    
    setDeeds(prev => {
      const currentCount = prev[today] || 0;
      const newCount = currentCount + 1;
      updatedDeeds = { ...prev, [today]: newCount };
      
      localStorage.setItem('habl_footprint', JSON.stringify(updatedDeeds));
      return updatedDeeds;
    });

    await mintCoin('deed', description, 1);
  };

  const getIntensity = (count: number) => {
    if (count === 0) return 'bg-slate-800';
    if (count === 1) return 'bg-emerald-900'; 
    if (count === 2) return 'bg-emerald-600'; 
    return 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]'; 
  };

  return { deeds, hablCoins, transactions, logDeed, mintCoin, getIntensity, syncWithCloud, isSyncing, setHablCoins };
}
